import { debounce } from 'lodash';
import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import useResizeObserver from 'use-resize-observer';

export const useScrollableTabs = () => {
  const tabListRef = useRef<HTMLDivElement | null>(null);

  const scrollToActiveTab = useCallback(
    (scrollIntoViewOptions?: ScrollIntoViewOptions) => {
      const activeTab = tabListRef.current?.querySelector<HTMLButtonElement>(
        '[aria-selected="true"]'
      );

      if (activeTab) {
        activeTab.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest',
          ...scrollIntoViewOptions,
        });
      }
    },
    []
  );

  // Create debounced function for resize handling
  const debouncedScrollToActiveTab = useMemo(
    () => debounce(() => scrollToActiveTab(), 100),
    [scrollToActiveTab]
  );

  // Handle resize events
  useResizeObserver({
    ref: tabListRef as RefObject<HTMLDivElement>,
    onResize: debouncedScrollToActiveTab,
  });

  // Watch for active tab changes and scroll into view
  useEffect(() => {
    const tabListElement = tabListRef.current;
    if (!tabListElement) return;

    const observer = new MutationObserver(() => {
      scrollToActiveTab();
    });

    observer.observe(tabListElement, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-selected'],
    });

    return () => {
      observer.disconnect();
    };
  }, [scrollToActiveTab]);

  // Initial scroll to active on mount
  useEffect(() => {
    scrollToActiveTab({ behavior: 'instant' });

    return () => {
      debouncedScrollToActiveTab.cancel();
    };
  }, [scrollToActiveTab, debouncedScrollToActiveTab]);

  return {
    tabListRef,
    scrollToActiveTab,
  };
};
